import styled from '@emotion/styled';
import React, { useCallback, useMemo } from 'react';
import ServerError from '../../../../../components/ServerError';
import { useNamespacedTranslation } from '../../../../../hooks/useNamespacedTranslation';
import SkeletonCard from '../../../components/Skeleton/Card';
import { useFlairLocalStorage } from '../../../hooks/useFlairLocalStorage';
import { useEmployeeSkills } from '../common/useEmployeeAvailableSkills';
import { buildSkillGroups } from '../common/skillGrouping';
import { mapChartSkill } from './mappings';
import { RadarGroupingToggle } from './RadarGroupingToggle';
import { SkillsRadarChart } from './SkillsRadarChart';

type Props = {
  employeeId: string;
};

const MIN_SPOKES = 3;

export const SkillsRadarChartConnected: React.FC<
  React.PropsWithChildren<Props>
> = ({ employeeId }) => {
  const { data, loading, error } = useEmployeeSkills(employeeId);
  const t = useNamespacedTranslation('skills.employeePage');
  const tGrouping = useNamespacedTranslation('skills.grouping');
  const [grouping, setGrouping] = useFlairLocalStorage(
    'skills_radar_grouping',
    'skill',
  );

  const resolveSkillName = useCallback(
    (skillId: string) =>
      data?.skills.find((s) => s.Id === skillId)?.localizedName,
    [data],
  );

  const chartSkills = useMemo(() => {
    if (!data) {
      return undefined;
    }
    const associations = data.activeEmployees.flatMap((x) => x.employeeSkills);
    const rated = associations.filter((s) => s.flair__Rate__c);
    const forChart =
      grouping === 'skill' ? rated.filter((s) => s.flair__Top__c) : rated;
    return forChart.map(mapChartSkill);
  }, [data, grouping]);

  const spokeCount = useMemo(() => {
    if (!chartSkills) {
      return 0;
    }
    return grouping === 'skill'
      ? chartSkills.length
      : buildSkillGroups(chartSkills, grouping, resolveSkillName).length;
  }, [chartSkills, grouping, resolveSkillName]);

  if (error) {
    return <ServerError />;
  }
  if (loading || !chartSkills) {
    return <SkeletonCard height={250} />;
  }

  return (
    <div>
      <div className="d-flex justify-content-end mb-4">
        <RadarGroupingToggle value={grouping} onChange={setGrouping} />
      </div>
      {spokeCount < MIN_SPOKES ? (
        <EmptyContainer className="small text-center">
          {grouping === 'skill'
            ? t('chartNotEnoughSkills')
            : tGrouping('notEnoughGroups')}
        </EmptyContainer>
      ) : (
        <SkillsRadarChart
          skills={chartSkills}
          grouping={grouping}
          resolveSkillName={resolveSkillName}
        />
      )}
    </div>
  );
};

const EmptyContainer = styled.div({
  width: '100%',
  height: '250px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});
